import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    Card,
    CircularProgress,
    Alert,
    Tabs,
    Tab,
    Box,
    TextField,
    Select,
    MenuItem,
    FormControl,
    Grid,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    FormHelperText,
    Tooltip
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { GET, PUT } from "APIclient";
import properties from "constants/ApplicationConstants";

const RATING_LEGEND = [
    { points: 1, rating: "Unsatisfactory", comment: "Performance is consistently non acceptable" },
    { points: 2, rating: "Below Expectations", comment: "Performance fails to meet job requirement on frequent basis" },
    { points: 3, rating: "Meets Expectations", comment: "Performance is regularly competent & dependable" },
    { points: 4, rating: "Exceeds Expectations", comment: "Performance is routinely above expectation" },
    { points: 5, rating: "Outstanding", comment: "Performance is consistently superior" },
];

const ROW_HEIGHT = 36;

const colStyle = { display: "flex", alignItems: "center", minHeight: ROW_HEIGHT };

function TabPanel({ children, value, index }) {
    return (
        <div role="tabpanel" hidden={value !== index}>
            {value === index && <Box p={0.5}>{children}</Box>}
        </div>
    );
}

function ManagerRatingForm() {
    const navigate = useNavigate();
    const { appraisalId, empId } = useParams();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [savingDraft, setSavingDraft] = useState(false);
    const [tabValue, setTabValue] = useState(0);
    const [notification, setNotification] = useState({ open: false, message: "", type: "" });

    const [employeeData, setEmployeeData] = useState(null);
    const [appraisalData, setAppraisalData] = useState(null);
    const [managerId, setManagerId] = useState(null);

    const [hasEmployeeSubmitted, setHasEmployeeSubmitted] = useState(false);
    const [managerPermission, setManagerPermission] = useState(false);
    const [empPermission, setEmpPermission] = useState(false);

    const [goalsData, setGoalsData] = useState(null);
    const [viewData, setViewData] = useState(null);

    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [showValidationErrors, setShowValidationErrors] = useState(false);

    const [managerRatings, setManagerRatings] = useState({
        technicalPrimaryScores: [],
        technicalPrimaryComments: [],
        technicalSecondaryScores: [],
        technicalSecondaryComments: [],
        competencyPrimaryScores: [],
        competencyPrimaryComments: [],
        competencySecondaryScores: [],
        competencySecondaryComments: [],
    });

    const [commentErrors, setCommentErrors] = useState({
        technicalPrimaryComments: [],
        technicalSecondaryComments: [],
        competencyPrimaryComments: [],
        competencySecondaryComments: [],
    });

    const [autoSaving, setAutoSaving] = useState(false);
    const [lastAutoSaveTime, setLastAutoSaveTime] = useState(null);
    const autoSaveIntervalRef = useRef(null);
    const hasChangesRef = useRef(false);

    useEffect(() => {
        const storedEmpId = localStorage.getItem("empId") || sessionStorage.getItem("empId");
        if (storedEmpId) {
            const parsedManagerId = parseInt(storedEmpId);
            setManagerId(parsedManagerId);
            Promise.all([
                fetchEmployeeDetails(appraisalId, empId),
                fetchGoals(appraisalId, empId)
            ]);
        } else {
            showNotification("Manager ID not found", "error");
            setLoading(false);
        }
    }, [appraisalId, empId]);

    useEffect(() => {
        if (hasEmployeeSubmitted && managerPermission && !loading) {
            if (autoSaveIntervalRef.current) clearInterval(autoSaveIntervalRef.current);
            autoSaveIntervalRef.current = setInterval(() => { handleAutoSave(); }, 300000);
            return () => { if (autoSaveIntervalRef.current) clearInterval(autoSaveIntervalRef.current); };
        }
    }, [hasEmployeeSubmitted, managerPermission, loading, managerRatings]);

    const handleAutoSave = async () => {
        if (autoSaving || savingDraft || saving || !hasEmployeeSubmitted || !managerPermission) return;
        const hasCommentErrors = Object.values(commentErrors).some(errors => errors.some(e => e !== ''));
        if (hasCommentErrors) return;
        const hasData = Object.values(managerRatings).some(arr => arr.some(val => val !== '' && val !== null && val !== undefined));
        if (!hasData) return;
        try {
            setAutoSaving(true);
            const payload = buildPayload("draft");
            const response = await PUT(`manager/save-ratings`, payload);
            if (response.data?.statusCode === 200) { setLastAutoSaveTime(new Date()); showNotification("Draft auto-saved", "info"); }
        } catch (error) {
            console.error("Error auto-saving draft:", error);
        } finally { setAutoSaving(false); }
    };

    const fetchEmployeeDetails = async (appraisalId, employeeId) => {
        try {
            setLoading(true);
            const response = await GET(`manager/${appraisalId}/employee/${employeeId}/view`);
            if (response.data?.data) {
                const data = response.data.data;
                const employeeHasRatings = checkEmployeeSubmission(data);
                setHasEmployeeSubmitted(employeeHasRatings);
                setManagerPermission(data.managerPermission || false);
                setEmpPermission(data.empPermission || false);
                const nameParts = (data.employeeName || '').split(' ');
                const firstName = nameParts[0] || '';
                const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : '';
                const middleName = nameParts.length > 2 ? nameParts.slice(1, -1).join(' ') : '';
                setEmployeeData({
                    empId: data?.empId || '',
                    firstName, middleName, lastName,
                    gender: data.gender || '',
                    dateOfJoining: data.dateOfJoining || '',
                    totalExperience: data.totalExperience || '',
                    reportingSince: data.reportingSince || '',
                });
                setViewData(data);
                setAppraisalData(prev => ({ ...prev, questionnaire: data.questionnaire || [] }));
            } else if (response.data?.statusCode === 400) {
                showNotification(response.data.message, "error");
            }
        } catch (error) {
            console.error("Error fetching employee details:", error);
            showNotification(error.response?.data?.message || "Failed to load employee details", "error");
        }
    };

    const fetchGoals = async (appraisalId, employeeId) => {
        try {
            const response = await GET(`manager/${appraisalId}/employee/${employeeId}/goals`);
            if (response.data?.data) setGoalsData(response.data.data);
            else if (response.data?.statusCode === 400) showNotification(response.data.message, "error");
        } catch (error) {
            console.error("Error fetching goals:", error);
            showNotification(error.response?.data?.message || "Failed to load goals", "error");
        } finally { setLoading(false); }
    };

    const checkEmployeeSubmission = (data) => {
        const allSkills = [
            ...(data?.technicalSkills?.primary || []),
            ...(data?.technicalSkills?.secondary || []),
            ...(data?.competencySkills?.primary || []),
            ...(data?.competencySkills?.secondary || []),
        ];
        return allSkills.some(skill => skill.empScore !== null && skill.empScore !== undefined && skill.empScore !== '');
    };

    useEffect(() => { if (goalsData && viewData) mergeSkillsData(); }, [goalsData, viewData]);

    const mergeSkillsData = () => {
        const mergeSkills = (goalsSkills, viewSkills) =>
            (goalsSkills || []).map((goalSkill, index) => {
                const viewSkill = (viewSkills || [])[index] || {};
                return { skillName: goalSkill.skillSet, paFormId: goalSkill.paFormId, empScore: viewSkill.empScore || '', empComment: viewSkill.empComment || '', appraiserScore: viewSkill.appraiserScore || '', appraiserComment: viewSkill.appraiserComment || '' };
            });

        const transformedSkills = {
            technicalPrimarySkills: mergeSkills(goalsData?.technicalPrimary, viewData?.technicalSkills?.primary),
            technicalSecondarySkills: mergeSkills(goalsData?.technicalSecondary, viewData?.technicalSkills?.secondary),
            competencyPrimarySkills: mergeSkills(goalsData?.competencyPrimary, viewData?.competencySkills?.primary),
            competencySecondarySkills: mergeSkills(goalsData?.competencySecondary, viewData?.competencySkills?.secondary),
        };

        setAppraisalData(prev => ({ ...prev, ...transformedSkills }));
        initializeManagerRatings(transformedSkills);
    };

    const initializeManagerRatings = (data) => {
        setManagerRatings({
            technicalPrimaryScores: data?.technicalPrimarySkills.map(s => s.appraiserScore || ''),
            technicalPrimaryComments: data?.technicalPrimarySkills.map(s => s.appraiserComment || ''),
            technicalSecondaryScores: data?.technicalSecondarySkills.map(s => s.appraiserScore || ''),
            technicalSecondaryComments: data?.technicalSecondarySkills.map(s => s.appraiserComment || ''),
            competencyPrimaryScores: data?.competencyPrimarySkills.map(s => s.appraiserScore || ''),
            competencyPrimaryComments: data?.competencyPrimarySkills.map(s => s.appraiserComment || ''),
            competencySecondaryScores: data?.competencySecondarySkills.map(s => s.appraiserScore || ''),
            competencySecondaryComments: data?.competencySecondarySkills.map(s => s.appraiserComment || ''),
        });
        setCommentErrors({
            technicalPrimaryComments: data?.technicalPrimarySkills.map(() => ''),
            technicalSecondaryComments: data?.technicalSecondarySkills.map(() => ''),
            competencyPrimaryComments: data?.competencyPrimarySkills.map(() => ''),
            competencySecondaryComments: data?.competencySecondarySkills.map(() => ''),
        });
    };

    const showNotification = (message, type = "info") => {
        setNotification({ open: true, message, type });
        setTimeout(() => setNotification({ open: false, message: "", type: "" }), 4000);
    };

    const handleTabChange = (event, newValue) => setTabValue(newValue);
    const isCommentOptionalForScore = (score) => { const p = parseInt(score); return p === 3 || p === 4; };

    const validateText = (value) => {
        if (!value || value === "") return { valid: true, error: '' };
        if (value.trim() === '') return { valid: false, error: 'Cannot enter only spaces' };
        if (!/[a-zA-Z0-9]/.test(value)) return { valid: false, error: 'Cannot enter only special characters' };
        if (/^[0-9\s\W]*$/.test(value) && /[0-9]/.test(value) && !/[a-zA-Z]/.test(value)) return { valid: false, error: 'Cannot enter only numbers' };
        return { valid: true, error: '' };
    };

    const handleScoreChange = (category, index, value) => {
        if (!managerPermission) { showNotification("You don't have permission to edit this form", "error"); return; }
        if (!hasEmployeeSubmitted) { showNotification("Cannot add ratings until employee submits their assessment", "warning"); return; }
        setManagerRatings(prev => { const s = [...prev[`${category}Scores`]]; s[index] = value; return { ...prev, [`${category}Scores`]: s }; });
        hasChangesRef.current = true;
    };

    const handleCommentChange = (category, index, value) => {
        if (!managerPermission) { showNotification("You don't have permission to edit this form", "error"); return; }
        if (!hasEmployeeSubmitted) { showNotification("Cannot add ratings until employee submits their assessment", "warning"); return; }
        const validation = validateText(value);
        setCommentErrors(prev => { const e = [...prev[`${category}Comments`]]; e[index] = validation.error; return { ...prev, [`${category}Comments`]: e }; });
        if (validation.valid || value === '') {
            setManagerRatings(prev => { const c = [...prev[`${category}Comments`]]; c[index] = value; return { ...prev, [`${category}Comments`]: c }; });
            hasChangesRef.current = true;
        }
    };

    const validateRatings = () => {
        const categories = ['technicalPrimaryScores', 'technicalSecondaryScores', 'competencyPrimaryScores', 'competencySecondaryScores'];
        for (const category of categories) {
            if (managerRatings[category].some(score => !score || score === '')) { showNotification("Please provide scores for all skills", "error"); return false; }
        }
        const commentCategories = [
            { key: "technicalPrimaryComments", scoreKey: "technicalPrimaryScores", name: "Technical Primary Skills" },
            { key: "technicalSecondaryComments", scoreKey: "technicalSecondaryScores", name: "Technical Secondary Skills" },
            { key: "competencyPrimaryComments", scoreKey: "competencyPrimaryScores", name: "Competency Primary Skills" },
            { key: "competencySecondaryComments", scoreKey: "competencySecondaryScores", name: "Competency Secondary Skills" },
        ];
        for (const { key, scoreKey, name } of commentCategories) {
            const missing = managerRatings[key].map((c, i) => (!c || !c.trim()) && !isCommentOptionalForScore(parseInt(managerRatings[scoreKey][i])) ? i + 1 : null).filter(Boolean);
            if (missing.length > 0) { showNotification(`Please provide comments for all ${name}. Missing: ${missing.join(", ")}`, "error"); if (key.includes("technical")) setTabValue(0); else setTabValue(1); return false; }
        }
        if (Object.values(commentErrors).some(errors => errors.some(e => e !== ''))) { showNotification("Please fix all comment errors before submitting", "error"); return false; }
        return true;
    };

    const buildPayload = (status) => ({
        appraisalId: parseInt(appraisalId),
        empId: parseInt(empId),
        ratingType: "MANAGER",
        technicalRatings: {
            primaryScores: managerRatings.technicalPrimaryScores.map(s => s ? parseInt(s) : null),
            primaryComments: managerRatings.technicalPrimaryComments,
            primarySkillSetIds: appraisalData?.technicalPrimarySkills.map(s => s.paFormId),
            secondaryScores: managerRatings.technicalSecondaryScores.map(s => s ? parseInt(s) : null),
            secondaryComments: managerRatings.technicalSecondaryComments,
            secondarySkillSetIds: appraisalData?.technicalSecondarySkills.map(s => s.paFormId),
        },
        competencyRatings: {
            primaryScores: managerRatings.competencyPrimaryScores.map(s => s ? parseInt(s) : null),
            primaryComments: managerRatings.competencyPrimaryComments,
            primarySkillSetIds: appraisalData?.competencyPrimarySkills.map(s => s.paFormId),
            secondaryScores: managerRatings.competencySecondaryScores.map(s => s ? parseInt(s) : null),
            secondaryComments: managerRatings.competencySecondaryComments,
            secondarySkillSetIds: appraisalData?.competencySecondarySkills.map(s => s.paFormId),
        },
        submitStatus: status
    });

    const handleSaveRatings = async () => {
        if (!managerPermission) { showNotification("You don't have permission to edit this form", "error"); return; }
        if (!hasEmployeeSubmitted) { showNotification("Employee must submit their self-assessment before you can provide ratings", "error"); return; }
        setShowValidationErrors(true);
        if (!validateRatings()) return;
        setOpenConfirmDialog(true);
    };

    const handleConfirmSubmit = async () => {
        setOpenConfirmDialog(false);
        try {
            setSaving(true);
            const payload = buildPayload("save");
            const response = await PUT(`manager/save-ratings`, payload);
            if (response.data?.statusCode === 200) { showNotification("Ratings saved successfully", "success"); if (autoSaveIntervalRef.current) clearInterval(autoSaveIntervalRef.current); setTimeout(() => navigate(-1), 2000); }
            if (response.data?.statusCode === 400) { showNotification(response.data.message, "error"); setTimeout(() => navigate(-1), 2000); }
        } catch (error) {
            showNotification(error.response?.data?.message || "Failed to save ratings", "error");
        } finally { setSaving(false); }
    };

    const handleSaveAsDraft = async () => {
        if (!managerPermission) { showNotification("You don't have permission to edit this form", "error"); return; }
        if (!hasEmployeeSubmitted) { showNotification("Employee must submit their self-assessment before you can save a draft", "warning"); return; }
        if (Object.values(commentErrors).some(errors => errors.some(e => e !== ''))) { showNotification("Please fix all comment errors before saving draft", "error"); return; }
        try {
            setSavingDraft(true);
            const payload = buildPayload("draft");
            const response = await PUT(`manager/save-ratings`, payload);
            if (response.data?.statusCode === 200) showNotification("Draft saved successfully", "success");
            if (response.data?.statusCode === 400) showNotification(response.data.message, "error");
        } catch (error) {
            showNotification(error.response?.data?.message || "Failed to save draft", "error");
        } finally { setSavingDraft(false); }
    };

    // Shared styles matching EmployeePerformanceForm
    const scoreSx = {
        height: ROW_HEIGHT,
        fontSize: "0.8rem",
        "& .MuiSelect-select": { py: "6px", color: "#000 !important" },
        "&.Mui-disabled .MuiSelect-select": { color: "#000 !important", WebkitTextFillColor: "#000 !important" },
    };

    const commentSx = {
        "& .MuiOutlinedInput-root": { height: ROW_HEIGHT, alignItems: "center" },
        "& .MuiInputBase-input": { py: "6px", fontSize: "0.8rem", color: "#000 !important", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
        "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: "#000 !important", color: "#000 !important" },
    };

    const renderSkillsTable = (skills, title, category) => {
        if (!skills?.length) {
            return <Alert severity="warning" sx={{ my: 0.5 }}><MDTypography variant="body2">No {title} Added.</MDTypography></Alert>;
        }

        const scoresKey = `${category}Scores`;
        const commentsKey = `${category}Comments`;

        // Column widths matching EmployeePerformanceForm layout
        const COL = { num: 0.4, skill: 2.2, empScore: 1.2, empComment: 3.4, mgrScore: 1.2, mgrComment: 3.6 };
        const headerCellSx = { fontSize: "0.72rem", fontWeight: 700, color: "#444", lineHeight: 1.2 };

        return (
            <Box sx={{ border: "1px solid #ddd", borderRadius: 1, overflow: "hidden", mb: 1 }}>
                {/* Header */}
                <Grid container sx={{ backgroundColor: "#f0f4f8", px: 0.75, py: 0.5 }} alignItems="center">
                    <Grid item xs={COL.num} sx={colStyle}><Typography sx={headerCellSx}>#</Typography></Grid>
                    <Grid item xs={COL.skill} sx={colStyle}><Typography sx={headerCellSx}>Skill Set</Typography></Grid>
                    <Grid item xs={COL.empScore} sx={{ ...colStyle, justifyContent: "center" }}>
                        <Typography sx={{ ...headerCellSx, textAlign: "center" }}>Emp. Score</Typography>
                    </Grid>
                    <Grid item xs={COL.empComment} sx={colStyle}>
                        <Typography sx={headerCellSx}>Emp. Comments</Typography>
                    </Grid>
                    <Grid item xs={COL.mgrScore} sx={{ ...colStyle, justifyContent: "center" }}>
                        <Typography sx={{ ...headerCellSx, textAlign: "center" }}>
                            Mgr Score<span style={{ color: "red" }}> *</span>
                        </Typography>
                    </Grid>
                    <Grid item xs={COL.mgrComment} sx={colStyle}>
                        <Typography sx={headerCellSx}>
                            Manager Comment<span style={{ color: "red", fontSize: "0.65rem" }}> * (required for score 1,2,5)</span>
                        </Typography>
                    </Grid>
                </Grid>

                {/* Rows */}
                {skills.map((skill, index) => {
                    const currentScore = managerRatings[scoresKey][index];
                    const isOptional = isCommentOptionalForScore(currentScore);
                    const currentComment = managerRatings[commentsKey][index] || "";
                    const commentError = commentErrors[commentsKey]?.[index] || "";
                    const showCommentError = !!commentError || (showValidationErrors && currentComment === "" && hasEmployeeSubmitted && managerPermission && !isOptional);
                    const commentHelperText = commentError ? commentError : showValidationErrors && currentComment === "" && hasEmployeeSubmitted && managerPermission && !isOptional ? "Required" : "";

                    return (
                        <Grid
                            container
                            key={index}
                            alignItems="center"
                            sx={{
                                px: 0.75,
                                py: 0.25,
                                backgroundColor: index % 2 === 0 ? "#fff" : "#fafafa",
                                borderTop: "1px solid #eee",
                                "&:hover": { backgroundColor: "#f5f9ff" },
                                minHeight: ROW_HEIGHT + (commentHelperText ? 18 : 0),
                            }}
                        >
                            <Grid item xs={COL.num} sx={colStyle}>
                                <Typography variant="body2" sx={{ fontSize: "0.78rem", color: "#666" }}>{index + 1}</Typography>
                            </Grid>

                            <Grid item xs={COL.skill} sx={colStyle}>
                                <Typography variant="body2" sx={{ fontSize: "0.8rem", wordBreak: "break-word", lineHeight: 1.3 }}>
                                    {skill.skillName}
                                </Typography>
                            </Grid>

                            {/* Employee Score (read-only) */}
                            <Grid item xs={COL.empScore} sx={{ ...colStyle, justifyContent: "center" }}>
                                <FormControl size="small" disabled sx={{ width: 68 }}>
                                    <Select value={empPermission ? "" : (skill.empScore || "")} displayEmpty sx={scoreSx} MenuProps={{ disableScrollLock: true }}>
                                        <MenuItem value="" sx={{ fontSize: "0.8rem" }}>-</MenuItem>
                                        {[1, 2, 3, 4, 5].map(s => <MenuItem key={s} value={s} sx={{ fontSize: "0.8rem" }}>{s}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* Employee Comment (read-only) */}
                            <Grid item xs={COL.empComment} sx={colStyle}>
                                <Tooltip
                                    title={!empPermission ? (skill.empComment || "") : ""}
                                    placement="top"
                                    arrow
                                    disableHoverListener={empPermission || !skill.empComment}
                                >
                                    <span style={{ width: "100%" }}>
                                        <TextField
                                            fullWidth
                                            value={empPermission ? "" : (skill.empComment || "")}
                                            disabled
                                            size="small"
                                            variant="outlined"
                                            placeholder="-"
                                            sx={commentSx}
                                            inputProps={{ style: { fontSize: "0.8rem" } }}
                                        />
                                    </span>
                                </Tooltip>
                            </Grid>

                            {/* Manager Score */}
                            <Grid item xs={COL.mgrScore} sx={{ ...colStyle, justifyContent: "center" }}>
                                <FormControl size="small" disabled={empPermission} sx={{ width: 68 }}>
                                    <Select
                                        value={currentScore || ""}
                                        onChange={(e) => handleScoreChange(category, index, e.target.value)}
                                        displayEmpty
                                        MenuProps={{ disableScrollLock: true }}
                                        sx={scoreSx}
                                    >
                                        <MenuItem value="" sx={{ fontSize: "0.8rem" }}>-</MenuItem>
                                        {[1, 2, 3, 4, 5].map(s => <MenuItem key={s} value={s} sx={{ fontSize: "0.8rem" }}>{s}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* Manager Comment */}
                            <Grid item xs={COL.mgrComment} sx={{ ...colStyle, flexDirection: "column", alignItems: "stretch" }}>
                                <Tooltip
                                    title={currentComment || ""}
                                    placement="top"
                                    arrow
                                    disableHoverListener={!currentComment}
                                >
                                    <span style={{ width: "100%" }}>
                                        <TextField
                                            fullWidth
                                            value={currentComment}
                                            onChange={(e) => handleCommentChange(category, index, e.target.value)}
                                            size="small"
                                            variant="outlined"
                                            disabled={savingDraft || saving || !hasEmployeeSubmitted || !managerPermission}
                                            placeholder={!hasEmployeeSubmitted ? "Awaiting employee submission" : isOptional ? "Optional" : "Required"}
                                            error={showCommentError}
                                            sx={commentSx}
                                            inputProps={{ style: { fontSize: "0.8rem" } }}
                                        />
                                    </span>
                                </Tooltip>
                                {commentHelperText && (
                                    <FormHelperText error sx={{ mx: 0, mt: 0, fontSize: "0.65rem", lineHeight: 1.2 }}>
                                        {commentHelperText}
                                    </FormHelperText>
                                )}
                            </Grid>
                        </Grid>
                    );
                })}
            </Box>
        );
    };

    const renderQuestionnaire = (questionnaire) => {
        if (!questionnaire?.length) {
            return <Alert severity="warning"><MDTypography variant="body2">No Questionnaire Added.</MDTypography></Alert>;
        }
        return (
            <MDBox>
                {questionnaire.map((item, index) => (
                    <MDBox key={index} mb={0.75}>
                        <MDTypography variant="body2" mb={0.25} color="dark" fontWeight="medium" sx={{ fontSize: "0.82rem" }}>
                            {item.question}
                        </MDTypography>
                        <TextField
                            fullWidth multiline rows={2}
                            value={!empPermission ? item.description : ""}
                            disabled variant="outlined" size="small"
                            placeholder="No answer provided"
                            sx={{
                                "& .MuiInputBase-input": { color: "#000 !important", fontSize: "0.82rem" },
                                "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: "#000 !important" },
                            }}
                        />
                    </MDBox>
                ))}
            </MDBox>
        );
    };

    // Compact info field matching EmployeePerformanceForm style
    const InfoField = ({ label, value, xs = 12, sm = 6, md = 3 }) => (
        <Grid item xs={xs} sm={sm} md={md}>
            <MDBox
                px={0.75} py={0.4}
                sx={{
                    backgroundColor: '#fff',
                    borderRadius: 1,
                    border: '1px solid #e0e0e0',
                    minHeight: 38,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                }}
            >
                <Typography sx={{ fontSize: "0.6rem", fontWeight: 700, color: "#888", textTransform: "uppercase", mb: 0.1, lineHeight: 1.2 }}>
                    {label}
                </Typography>
                <Typography sx={{ fontSize: "0.82rem", fontWeight: 500, color: "#222", lineHeight: 1.3 }}>
                    {value || '-'}
                </Typography>
            </MDBox>
        </Grid>
    );

    if (loading) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                    <CircularProgress />
                </MDBox>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={0.5} pb={1.5}>
                <Card>
                    {/* Header */}
                    <MDBox
                        variant="gradient"
                        sx={{ background: properties.colors.bgcolorbluethem }}
                        bgColor="info"
                        borderRadius="lg"
                        coloredShadow="success"
                        mx={1.5}
                        mt={0.75}
                        p={0.75}
                        textAlign="center"
                    >
                        <MDTypography variant="h5" fontWeight="medium" color="white" sx={{ fontSize: "1rem" }}>
                            Manager Rating Form
                        </MDTypography>
                        <MDTypography variant="body2" color="white" opacity={0.85} sx={{ fontSize: "0.75rem" }}>
                            {managerPermission ? "Provide ratings and feedback for employee appraisal" : "View Only - You don't have permission to edit this form"}
                        </MDTypography>
                        {hasEmployeeSubmitted && managerPermission && (
                            <MDBox display="flex" alignItems="center" justifyContent="center" mt={0.25}>
                                {autoSaving ? (
                                    <MDBox display="flex" alignItems="center" gap={0.5}>
                                        <CircularProgress size={12} color="inherit" sx={{ color: 'white' }} />
                                        <MDTypography variant="caption" color="white" sx={{ fontSize: '0.7rem' }}>Auto-saving...</MDTypography>
                                    </MDBox>
                                ) : lastAutoSaveTime && (
                                    <MDTypography variant="caption" color="white" opacity={0.9} sx={{ fontSize: '0.7rem' }}>
                                        Last auto-saved: {lastAutoSaveTime.toLocaleTimeString()}
                                    </MDTypography>
                                )}
                            </MDBox>
                        )}
                    </MDBox>

                    <MDBox p={0.75}>
                        {empPermission && (
                            <Alert severity="warning" sx={{ mb: 0.75, py: 0.25, "& .MuiAlert-message": { py: 0.25 } }}>
                                <MDTypography variant="body2" sx={{ fontSize: "0.85rem" }}>
                                    The employee has not yet submitted their self-assessment. You can view the form but cannot add ratings until the employee completes their submission.
                                </MDTypography>
                            </Alert>
                        )}

                        {/* Employee Official Details */}
                        <Card variant="outlined" sx={{ mb: 0.75 }}>
                            <MDBox px={0.75} pt={0.5} pb={0.25} sx={{ backgroundColor: "#f7f7f7", borderBottom: "1px solid #e0e0e0" }}>
                                <MDTypography variant="subtitle2" fontWeight="bold" sx={{ fontSize: "0.82rem" }}>
                                    Official Details
                                </MDTypography>
                            </MDBox>
                            <MDBox px={0.75} py={0.5}>
                                <Grid container spacing={0.5}>
                                    <InfoField label="Employee ID" value={employeeData?.empId} xs={12} sm={6} md={3} />
                                    <InfoField label="First Name" value={employeeData?.firstName} xs={12} sm={6} md={3} />
                                    <InfoField label="Middle Name" value={employeeData?.middleName} xs={12} sm={6} md={3} />
                                    <InfoField label="Last Name" value={employeeData?.lastName} xs={12} sm={6} md={3} />
                                    <InfoField label="Gender" value={employeeData?.gender} xs={12} sm={6} md={3} />
                                    <InfoField
                                        label="Date of Joining"
                                        value={employeeData?.dateOfJoining ? new Date(employeeData.dateOfJoining).toLocaleDateString() : '-'}
                                        xs={12} sm={6} md={3}
                                    />
                                    <InfoField label="Total Experience (Yrs)" value={employeeData?.totalExperience} xs={12} sm={6} md={3} />
                                    <InfoField
                                        label="Reporting Since"
                                        value={employeeData?.reportingSince}
                                        xs={12} sm={6} md={3}
                                    />
                                </Grid>
                            </MDBox>
                        </Card>

                        {/* Ratings Legend */}
                        <Card variant="outlined" sx={{ mb: 0.75 }}>
                            <MDBox px={0.75} pt={0.5} pb={0.25} sx={{ backgroundColor: "#f7f7f7", borderBottom: "1px solid #e0e0e0" }}>
                                <MDTypography variant="subtitle1" fontWeight="bold" sx={{ fontSize: "0.85rem" }}>
                                    Performance Appraisal Form (Ratings)
                                </MDTypography>
                            </MDBox>
                            <MDBox px={0.75} py={0.5}>
                                <MDTypography variant="body2" mb={0.5} color="black" sx={{ fontSize: "0.78rem" }}>
                                    Use the following ratings for consistency.
                                </MDTypography>
                                <Grid container sx={{ backgroundColor: "#f0f4f8", px: 0.5, py: 0.3, borderRadius: "4px 4px 0 0", border: "1px solid #ddd" }}>
                                    <Grid item xs={1}><Typography sx={{ fontSize: "0.72rem", fontWeight: 700 }}>Points</Typography></Grid>
                                    <Grid item xs={2.5}><Typography sx={{ fontSize: "0.72rem", fontWeight: 700 }}>Ratings</Typography></Grid>
                                    <Grid item xs={8.5}><Typography sx={{ fontSize: "0.72rem", fontWeight: 700 }}>Comments</Typography></Grid>
                                </Grid>
                                {RATING_LEGEND.map((row, index) => (
                                    <Grid
                                        container key={row.points}
                                        sx={{
                                            px: 0.5, py: 0.2,
                                            backgroundColor: index % 2 === 0 ? "#fff" : "#fafafa",
                                            border: "1px solid #ddd", borderTop: "none",
                                            borderRadius: index === RATING_LEGEND.length - 1 ? "0 0 4px 4px" : 0,
                                        }}
                                        alignItems="center"
                                    >
                                        <Grid item xs={1}><Typography sx={{ fontSize: "0.78rem" }}>{row.points}</Typography></Grid>
                                        <Grid item xs={2.5}><Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: "#222" }}>{row.rating}</Typography></Grid>
                                        <Grid item xs={8.5}><Typography sx={{ fontSize: "0.78rem", color: "#333" }}>{row.comment}</Typography></Grid>
                                    </Grid>
                                ))}
                            </MDBox>
                        </Card>

                        {/* Tabs */}
                        <Card variant="outlined" sx={{ mb: 0.75 }}>
                            <Box sx={{ borderBottom: "1px solid #ddd" }}>
                                <Tabs
                                    value={tabValue}
                                    onChange={handleTabChange}
                                    textColor="info"
                                    indicatorColor="info"
                                    variant="fullWidth"
                                    sx={{
                                        minHeight: 38,
                                        "& .MuiTab-root": { textTransform: "none", fontWeight: 500, fontSize: "0.82rem", minHeight: 38, py: 0 },
                                        "& .Mui-selected": { color: "#2196f3 !important" },
                                    }}
                                >
                                    <Tab label="Technical Skills" />
                                    <Tab label="Performance Competency" />
                                    <Tab label="Questionnaire" />
                                </Tabs>
                            </Box>

                            <TabPanel value={tabValue} index={0}>
                                <MDTypography variant="subtitle2" fontWeight="bold" mb={0.25} sx={{ fontSize: "0.82rem" }}>
                                    Technical Primary Skills
                                </MDTypography>
                                {renderSkillsTable(appraisalData?.technicalPrimarySkills, "Technical Primary Skills", "technicalPrimary")}
                                <MDTypography variant="subtitle2" fontWeight="bold" mb={0.25} sx={{ fontSize: "0.82rem" }}>
                                    Technical Secondary Skills
                                </MDTypography>
                                {renderSkillsTable(appraisalData?.technicalSecondarySkills, "Technical Secondary Skills", "technicalSecondary")}
                            </TabPanel>

                            <TabPanel value={tabValue} index={1}>
                                <MDTypography variant="subtitle2" fontWeight="bold" mb={0.25} sx={{ fontSize: "0.82rem" }}>
                                    Competency Primary Skills
                                </MDTypography>
                                {renderSkillsTable(appraisalData?.competencyPrimarySkills, "Competency Primary Skills", "competencyPrimary")}
                                <MDTypography variant="subtitle2" fontWeight="bold" mb={0.25} sx={{ fontSize: "0.82rem" }}>
                                    Competency Secondary Skills
                                </MDTypography>
                                {renderSkillsTable(appraisalData?.competencySecondarySkills, "Competency Secondary Skills", "competencySecondary")}
                            </TabPanel>

                            <TabPanel value={tabValue} index={2}>
                                <MDTypography variant="subtitle2" fontWeight="bold" mb={0.5} sx={{ fontSize: "0.82rem" }}>
                                    Questionnaire
                                </MDTypography>
                                {renderQuestionnaire(appraisalData?.questionnaire)}
                            </TabPanel>
                        </Card>

                        {/* Action Buttons */}
                        <MDBox display="flex" justifyContent="space-between" alignItems="center" mt={0.5}>
                            <MDButton variant="outlined" color="secondary" size="small" onClick={() => navigate(-1)}>
                                Cancel
                            </MDButton>
                            <MDButton
                                variant="gradient"
                                color="success"
                                size="small"
                                onClick={handleSaveRatings}
                                disabled={empPermission || !managerPermission}
                                sx={{ background: properties.colors.bgcolorbluethem, mr: 20 }}
                            >
                                {saving ? <CircularProgress size={16} color="inherit" /> : "Save Ratings"}
                            </MDButton>
                        </MDBox>
                    </MDBox>
                </Card>

                {/* Fixed Save as Draft Button */}
                <MDBox sx={{ position: "fixed", bottom: 16, right: 20, zIndex: 1200 }}>
                    <MDButton
                        variant="gradient"
                        color="info"
                        size="small"
                        onClick={handleSaveAsDraft}
                        disabled={empPermission || !managerPermission}
                        sx={{ background: properties.colors.bgcolorbluethem, boxShadow: "0px 4px 12px rgba(0,0,0,0.25)", px: 2, py: 0.75 }}
                    >
                        {savingDraft ? <CircularProgress size={16} color="inherit" /> : "💾 Save as Draft"}
                    </MDButton>
                </MDBox>

                {/* Confirmation Dialog */}
                <Dialog open={openConfirmDialog} onClose={() => setOpenConfirmDialog(false)} PaperProps={{ sx: { borderRadius: 2, minWidth: 360 } }} disableScrollLock>
                    <DialogTitle sx={{ fontWeight: "bold", color: "#1976d2", pb: 0.5, fontSize: "1rem" }}>
                        Confirm Submission
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText sx={{ color: "#333", fontSize: "0.88rem" }}>
                            Are you sure you want to submit your manager ratings?
                            <br /><br />
                            <strong style={{ color: "#d32f2f" }}>Warning:</strong> Once submitted, you will not be able to edit this form again.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions sx={{ px: 2, pb: 1.25 }}>
                        <MDButton variant="outlined" color="secondary" size="small" onClick={() => setOpenConfirmDialog(false)}>Cancel</MDButton>
                        <MDButton variant="gradient" color="success" size="small" onClick={handleConfirmSubmit} sx={{ background: properties.colors.bgcolorbluethem }}>
                            Yes, Submit
                        </MDButton>
                    </DialogActions>
                </Dialog>

                {/* Notification */}
                {notification.open && (
                    <MDBox sx={{ position: "fixed", top: 16, right: 16, zIndex: 9999, minWidth: 280 }}>
                        <Alert severity={notification.type} onClose={() => setNotification({ open: false, message: "", type: "" })} sx={{ fontSize: "0.82rem" }}>
                            {notification.message}
                        </Alert>
                    </MDBox>
                )}
            </MDBox>
        </DashboardLayout>
    );
}

export default ManagerRatingForm;